import React, { useEffect, useRef } from "react";
import { Box, Button, IconButton, Flex } from "@chakra-ui/react";
import { Chart, ChartTypeRegistry } from "chart.js/auto";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import { Vital } from "requests";
import dbpIcon from "./assets/diastolicBloodPressurePoint.svg";
import endTidalPointIcon from "./assets/endTidalPoint.svg";
import hrIcon from "./assets/hrPoint.svg";
import mbpIcon from "./assets/meanBloodPressurePoint.svg";
import spIcon from "./assets/pulseOximetryPoint.svg";
import rrIcon from "./assets/rrPoint.svg";
import sbpIcon from "./assets/sysBloodPressurePoint.svg";
import {
  getMaxVitalsNumber,
  getTimeSortedVitals,
  getVitalDatasets,
  moveChartBack,
  moveChartForward,
  moveChartToCurrentTime,
  MAX_X_AXIS
} from "./duck";

interface LineChartProps {
  timeIntervals: string[];
  vitals?: Vital[];
}

const VitalsChart: React.FC<LineChartProps> = ({ timeIntervals, vitals }) => {
  const hrPoint = new Image();
  const rrPoint = new Image();
  const spPoint = new Image();
  const endTidalPoint = new Image();
  const sbpPoint = new Image();
  const mbpPoint = new Image();
  const dbpPoint = new Image();
  hrPoint.src = hrIcon;
  rrPoint.src = rrIcon;
  spPoint.src = spIcon;
  endTidalPoint.src = endTidalPointIcon;
  sbpPoint.src = sbpIcon;
  mbpPoint.src = mbpIcon;
  dbpPoint.src = dbpIcon;

  const [narrowed, setNarrowed] = React.useState(false);
  let maxXAxis = MAX_X_AXIS;
  if (narrowed) {
    maxXAxis = timeIntervals.length;
  }

  const maxValueOfVitals = getMaxVitalsNumber(vitals);
  const sortedVitalsByTime = getTimeSortedVitals(vitals);
  const {
    heartRateDataSet,
    respiratoryRateDataSet,
    pulseOximetryDataSet,
    endTidalDataSet,
    systolicBloodPressureDataSet,
    meanBloodPressureDataSet,
    diastolicBloodPressureDataSet
  } = getVitalDatasets(sortedVitalsByTime);

  const data = {
    labels: [...timeIntervals],
    datasets: [
      {
        label: "HR",
        data: heartRateDataSet,
        borderColor: "#86A3F6",
        pointStyle: narrowed ? false : hrPoint,
        borderWidth: 3
      },
      {
        label: "RR",
        data: respiratoryRateDataSet,
        borderColor: "#48BB78",
        pointStyle: narrowed ? false : rrPoint,
        borderWidth: 3
      },
      {
        label: "SpO2",
        data: pulseOximetryDataSet,
        borderColor: "#ECC94B",
        pointStyle: narrowed ? false : spPoint,
        borderWidth: 3
      },
      {
        label: "End-Tidal CO2",
        data: endTidalDataSet,
        borderColor: "#ED8936",
        pointStyle: narrowed ? false : endTidalPoint,
        borderWidth: 3
      },
      {
        label: "SBP",
        data: systolicBloodPressureDataSet,
        borderColor: "#F56565",
        pointStyle: narrowed ? false : sbpPoint,
        borderWidth: 3
      },
      {
        label: "MBP",
        data: meanBloodPressureDataSet,
        borderColor: "#9F7AEA",
        pointStyle: narrowed ? false : mbpPoint,
        borderWidth: 3
      },
      {
        label: "DBP",
        data: diastolicBloodPressureDataSet,
        borderColor: "#ED64A6",
        pointStyle: narrowed ? false : dbpPoint,
        borderWidth: 3
      }
    ]
  };

  const moveChart = {
    id: "moveChart",
    afterDraw(chart: Chart) {
      const {
        ctx,
        chartArea: { left, right, bottom, width }
      } = chart;

      ctx.beginPath();
      ctx.fillStyle = "lightgrey";
      ctx.rect(left, bottom + 40, width, 4);
      ctx.fill();
      ctx.closePath();

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const { min } = chart.options?.scales?.x;
      let startingPoint = left + (width / timeIntervals.length) * min;
      const barWidth = (width / timeIntervals.length) * (maxXAxis + 1);
      const totalWidth = startingPoint + barWidth;
      if (totalWidth > width) {
        startingPoint = right - barWidth;
      }

      ctx.beginPath();
      ctx.fillStyle = "black";
      ctx.rect(startingPoint, bottom + 40, barWidth, 4);
      ctx.fill();
      ctx.closePath();
    }
  };

  const config = {
    type: "line" as keyof ChartTypeRegistry,
    data,
    options: {
      scales: {
        x: {
          min: 0,
          max: maxXAxis,
          ticks: {
            maxRotation: 0,
            minRotation: 0
          }
        },
        y: {
          min: 0,
          max: maxValueOfVitals ? maxValueOfVitals + 20 : 200,
          beginAtZero: true
        }
      },
      layout: {
        padding: {
          bottom: narrowed ? 0 : 30
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            title: () => ""
          }
        }
      }
    },

    plugins: [moveChart]
  };

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);
  const intervalRef = React.useRef(null);

  useEffect(() => {
    if (canvasRef?.current) {
      chartRef.current = new Chart(canvasRef?.current, config);
    }
    return () => {
      chartRef?.current?.destroy();
    };
  }, [vitals, narrowed]);

  useEffect(() => {
    if (!narrowed) {
      moveChartToCurrentTime(chartRef.current, timeIntervals);

      const intervalId = setInterval(() => {
        moveChartToCurrentTime(chartRef.current, timeIntervals);
      }, 5 * 60 * 1000);

      return () => clearInterval(intervalId);
    }
  }, [timeIntervals, narrowed]);

  const moveBack = () => {
    if (intervalRef.current) return;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    intervalRef.current = setInterval(() => {
      moveChartBack(chartRef.current);
    }, 250);
  };

  const moveForward = () => {
    if (intervalRef.current) return;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    intervalRef.current = setInterval(() => {
      moveChartForward(chartRef.current);
    }, 250);
  };

  const stopMoving = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  return (
    <>
      <Flex justify="end" mb={4}>
        <Button
          disabled={!maxValueOfVitals}
          onClick={() => setNarrowed(!narrowed)}
        >
          {narrowed ? "Zoom In" : "Zoom Out"}
        </Button>
      </Flex>
      <Box position="relative">
        <canvas ref={canvasRef} />
        {!narrowed && (
          <>
            <IconButton
              variant="unstyled"
              position="absolute"
              aria-label="move chart back"
              bottom="-3px"
              left="10px"
              onMouseDown={moveBack}
              onMouseLeave={stopMoving}
              onMouseUp={stopMoving}
              onClick={() => moveChartBack(chartRef.current)}
              icon={<IoChevronBack />}
            />

            <IconButton
              variant="unstyled"
              position="absolute"
              aria-label="move chart forward"
              bottom="-3px"
              right="-20px"
              onMouseDown={moveForward}
              onMouseLeave={stopMoving}
              onMouseUp={stopMoving}
              onClick={() => moveChartForward(chartRef.current)}
              icon={<IoChevronForward />}
            />
          </>
        )}
      </Box>
    </>
  );
};

export default VitalsChart;
